import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import InlineTextInput from '@/components/ui/inline-text-input';
import { useAuthContext } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DealSelector } from '../deal/deal-selector';

// CreatedByID string `json:"created_by_id"`
// FullName    string `json:"full_name"`
// VIN         string `json:"vin"`
// Make        string `json:"make"`
// Model       string `json:"model"`
// Trim        string `json:"trim"`
// Year        int    `json:"year"`
// Mileage     int    `json:"mileage"`
type CreateTradePayload = {
  id: string;
  created_by_id: string;
  full_name: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  year: number;
  mileage: number;
  deal_id?: string;
};

const ROW_CLASSNAME = 'flex flex-row gap-2 items-center justify-between';

const CreateTradeDialog = ({
  open,
  onOpenChange,
  initialDealId = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDealId?: string;
}) => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  const createTradeMutation = useMutation({
    onMutate: async () =>
      // trade: CreateTradePayload
      {
        // Add the optimistic trade to the store
        // after allowing the BE accept id
        // tradeStore.add({
        //   ...trade,
        //   short_id: 'Optimistic-TIA',
        //   created_at: dayjs().toISOString(),
        //   updated_at: dayjs().toISOString(),
        //   condition: '',
        //   paid_off: false,
        //   lienholder: '',
        //   state: '',
        //   postal_code: '',
        //   number_of_keys: 0,
        //   asking_price: 0,
        //   exterior_color: '',
        //   interior_color: '',
        //   offer: 0,
        //   resell_type: 'none',
        //   status: 'requested',
        //   title_present: false,
        //   payoff_amount: 0,
        //   appraised_value: 0,
        //   ever_location: '',
        //   deal_id: trade.deal_id || '',
        // });
      },
    mutationFn: async (trade: CreateTradePayload) => {
      const response = await pdkAxios.post('/trades/create', trade);
      return response.data;
    },
    onSuccess: () => {
      onOpenChange(false);
      toast({
        title: 'Trade created',
        description: `Trade created successfully for ${customerName}`,
      });
    },
    onError: (error, variables) => {
      console.error('Error creating trade', error, variables);
      toast({
        title: 'Error creating trade',
        description: 'Please try again.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['trades'] });
    },
  });

  const [customerName, setCustomerName] = useState('');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [trim, setTrim] = useState('');
  const [year, setYear] = useState('');
  const [mileage, setMileage] = useState('');
  const [dealId, setDealId] = useState(initialDealId);

  const handleCreateTrade = () => {
    if (!user?.id) {
      throw new Error('User ID is required');
    }

    createTradeMutation.mutate({
      id: uuidv4(),
      created_by_id: user?.id || '',
      full_name: customerName,
      vin: vin,
      make: make,
      model: model,
      trim: trim,
      year: Number(year),
      mileage: Number(mileage),
      deal_id: dealId,
    });
  };
  const onClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onClickClose={() => {
          onOpenChange(false);
        }}
        className="!top-[0%] !mt-80 max-w-2xl"
      >
        <DialogTitle>Create Trade</DialogTitle>
        <DialogDescription>Create a trade in appraisal</DialogDescription>

        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Customer Name</label>
          <InlineTextInput
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Customer Name"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">VIN</label>
          <InlineTextInput
            value={vin}
            onChange={(e) => setVin(e.target.value)}
            placeholder="VIN"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Make</label>
          <InlineTextInput
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder="Make"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Model</label>
          <InlineTextInput
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Model"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Trim</label>
          <InlineTextInput
            value={trim}
            onChange={(e) => setTrim(e.target.value)}
            placeholder="Trim"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Year</label>
          <InlineTextInput
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="Year"
            className="text-right"
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Mileage</label>
          <InlineTextInput
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            placeholder="Mileage"
            className="text-right"
          />
        </div>

        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Deal</label>
          <DealSelector
            dealId={dealId}
            handleDealChange={(option) => {
              setDealId(option.split(' - ')[0]);
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" type="reset" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" type="reset" onClick={handleCreateTrade}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTradeDialog;
